import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { MessageRole } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { AskDocumentQuestion_MessageFragment } from '../graphql/AskDocumentQuestion.generated';
import { useStartDocumentQuestionStreamMutation } from '../graphql/StartDocumentQuestionStream.generated';
import { useSubscribeDocumentQuestionSubscription } from '../graphql/SubscribeDocumentQuestion.generated';

const TEMP_USER_MESSAGE_PREFIX = 'temp-user-';
const TEMP_ASSISTANT_MESSAGE_PREFIX = 'temp-assistant-';

interface DocumentChatContextValue {
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  clearChat: () => void;
  documentId: string | null;
  conversationId: string | null;
  messages: AskDocumentQuestion_MessageFragment[];
  askQuestion: (message: string) => Promise<void>;
  setDocumentId: (documentId: string | null | undefined) => void;
  awaitingFirstToken: boolean;
  error: string | null;
  lastUserMessage: string | null;
  streamingMessageId: string | null;
  /** Message ID to scroll to after user sends a message. ChatBar should consume and clear this. */
  pendingScrollToMessageId: string | null;
  clearPendingScroll: () => void;
}

const DocumentChatContext = createContext<DocumentChatContextValue | undefined>(
  undefined
);

export function useDocumentChatContext() {
  const context = useContext(DocumentChatContext);
  if (!context) {
    throw new Error(
      'useDocumentChatContext must be used within a DocumentChatProvider'
    );
  }
  return context;
}

export function useUnguardedDocumentChatContext() {
  return useContext(DocumentChatContext);
}

export function useOptionalDocumentChatContext() {
  return useContext(DocumentChatContext);
}

interface DocumentChatProviderProps {
  children: ReactNode;
  documentId?: string | null;
}

export function DocumentChatProvider({
  children,
  documentId: initialDocumentId = null,
}: DocumentChatProviderProps) {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [documentId, setDocumentId] = useState<string | null>(
    initialDocumentId ?? null
  );
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<
    AskDocumentQuestion_MessageFragment[]
  >([]);

  const [lastUserMessage, setLastUserMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null
  );
  const [streamingText, setStreamingText] = useState<string>('');
  const [workflowTrackerId, setWorkflowTrackerId] = useState<string | null>(
    null
  );
  const [pendingScrollToMessageId, setPendingScrollToMessageId] = useState<
    string | null
  >(null);

  // Refs to avoid stale closures in subscription callbacks
  const streamingMessageIdRef = useRef<string | null>(null);
  const streamingTextRef = useRef<string>('');

  // Tracks the in-flight question so we can emit a single PostHog event with
  // success/duration when the stream resolves, errors, or is superseded.
  const pendingTrackingRef = useRef<{
    query: string;
    startTime: number;
    isFollowUp: boolean;
  } | null>(null);

  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const trackUserEvent = useTrackUserEvent();

  const trackQuestionResult = useCallback(
    (success: boolean) => {
      const pending = pendingTrackingRef.current;
      if (!pending) return;
      pendingTrackingRef.current = null;
      trackUserEvent('document chat question asked', {
        query: pending.query,
        success,
        duration: Date.now() - pending.startTime,
        isFollowUp: pending.isFollowUp,
      });
    },
    [trackUserEvent]
  );

  const [startStream, { loading: mutationLoading }] =
    useStartDocumentQuestionStreamMutation({
      onError: (error) => {
        reportError('Failed to start stream', error);
        setError('Something went wrong, try again');
        // Remove optimistic messages on error
        setMessages((prev) =>
          prev.filter(
            (msg) =>
              !msg.id.startsWith(TEMP_USER_MESSAGE_PREFIX) &&
              !msg.id.startsWith(TEMP_ASSISTANT_MESSAGE_PREFIX)
          )
        );
        setStreamingMessageId(null);
        streamingMessageIdRef.current = null;
        setStreamingText('');
        streamingTextRef.current = '';
        trackQuestionResult(false);
        // Keep lastUserMessage so it can be used to repopulate the input
      },
      onCompleted: (data) => {
        const { workflowTracker, conversation } =
          data.startDocumentQuestionStream;

        // Update conversation ID if this is the first message (use functional update to avoid stale closure)
        if (conversation?.id) {
          setConversationId((prev) => prev ?? conversation.id);
        }

        // Start subscription with workflow tracker ID
        setWorkflowTrackerId(workflowTracker.id);
      },
    });

  // Subscribe to the document question stream
  useSubscribeDocumentQuestionSubscription({
    skip: !workflowTrackerId,
    variables: workflowTrackerId ? { workflowTrackerId } : undefined,
    onData: ({ data }) => {
      const event = data.data?.subscribeDocumentQuestion;
      if (!event) return;

      // Handle text delta - accumulate streaming text using refs to avoid stale closures
      if (event.textDelta) {
        const nextText = streamingTextRef.current + event.textDelta;
        streamingTextRef.current = nextText;
        setStreamingText(nextText);

        // Update the streaming message with accumulated text
        const currentStreamingId = streamingMessageIdRef.current;
        if (currentStreamingId) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === currentStreamingId
                ? { ...msg, content: nextText }
                : msg
            )
          );
        }
      }

      // Handle final result - replace with complete conversation
      if (event.result) {
        const messageNodes = getNodes(event.result.conversation?.messages);
        setMessages(messageNodes);
        setStreamingMessageId(null);
        streamingMessageIdRef.current = null;
        setStreamingText('');
        streamingTextRef.current = '';
        setWorkflowTrackerId(null);
        setError(null);
        setLastUserMessage(null); // Clear on success
        trackQuestionResult(true);
      }
    },
    onError: (error) => {
      reportError('Stream error', error);
      setError('Something went wrong with the stream');
      setStreamingMessageId(null);
      streamingMessageIdRef.current = null;
      setWorkflowTrackerId(null);
      trackQuestionResult(false);
      // Keep accumulated text in case user wants to see partial response
    },
  });

  const openChat = useCallback(() => {
    setIsChatOpen(true);
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    setLastUserMessage(null);
    setStreamingMessageId(null);
    streamingMessageIdRef.current = null;
    setStreamingText('');
    streamingTextRef.current = '';
    setWorkflowTrackerId(null);
  }, []);

  const askQuestion = useCallback(
    async (message: string) => {
      if (!documentId) {
        showFeedback('Document not selected');
        return;
      }

      // Cancel any existing stream
      if (workflowTrackerId) {
        setWorkflowTrackerId(null);
        setStreamingMessageId(null);
        streamingMessageIdRef.current = null;
        setStreamingText('');
        streamingTextRef.current = '';
        // The previous question was superseded before it resolved — emit a
        // failure event for it so the in-flight tracking state doesn't leak
        // into this new question.
        trackQuestionResult(false);
      }

      setError(null);
      setLastUserMessage(message);
      pendingTrackingRef.current = {
        query: message,
        startTime: Date.now(),
        isFollowUp: conversationId !== null,
      };

      // Add user message to UI immediately
      const userMessageId = `${TEMP_USER_MESSAGE_PREFIX}${Date.now()}`;
      const userMessage: AskDocumentQuestion_MessageFragment = {
        id: userMessageId,
        role: MessageRole.User,
        content: message,
      };

      // Add assistant message placeholder for streaming
      const assistantMessageId = `${TEMP_ASSISTANT_MESSAGE_PREFIX}${Date.now()}`;
      const assistantMessage: AskDocumentQuestion_MessageFragment = {
        id: assistantMessageId,
        role: MessageRole.Assistant,
        content: '',
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setPendingScrollToMessageId(userMessageId);
      setStreamingMessageId(assistantMessageId);
      streamingMessageIdRef.current = assistantMessageId;
      setStreamingText('');
      streamingTextRef.current = '';

      await startStream({
        variables: {
          input: {
            documentId,
            conversationID: conversationId,
            message,
          },
        },
      });
    },
    [
      documentId,
      conversationId,
      showFeedback,
      startStream,
      workflowTrackerId,
      trackQuestionResult,
    ]
  );

  const handleSetDocumentId = useCallback((id: string | null | undefined) => {
    setDocumentId(id ?? null);
  }, []);

  const clearPendingScroll = useCallback(() => {
    setPendingScrollToMessageId(null);
  }, []);

  // Show loading while mutation is running OR while streaming but no text received yet
  const awaitingFirstToken =
    mutationLoading || (!!streamingMessageId && streamingText === '');

  const value = useMemo(
    () => ({
      isChatOpen,
      openChat,
      closeChat,
      clearChat,
      documentId,
      conversationId,
      messages,
      askQuestion,
      setDocumentId: handleSetDocumentId,
      awaitingFirstToken,
      error,
      lastUserMessage,
      streamingMessageId,
      pendingScrollToMessageId,
      clearPendingScroll,
    }),
    [
      isChatOpen,
      openChat,
      closeChat,
      clearChat,
      documentId,
      conversationId,
      messages,
      askQuestion,
      handleSetDocumentId,
      awaitingFirstToken,
      error,
      lastUserMessage,
      streamingMessageId,
      pendingScrollToMessageId,
      clearPendingScroll,
    ]
  );

  return (
    <DocumentChatContext.Provider value={value}>
      {children}
    </DocumentChatContext.Provider>
  );
}
